import React from 'react';
import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const COLORS = {
  gold: '#f0c040',
  cyan: '#00d4ff',
  green: '#00e87a',
  red: '#ff4444',
  purple: '#b060ff',
  amber: '#ffb040',
  bg: '#04060f',
  cardBg: '#0a1020',
  muted: '#4a5a7a',
};

const SUB_SET_TYPES = [
  { value: 'base', label: 'BASE', color: COLORS.green },
  { value: 'insert', label: 'INSERT', color: COLORS.cyan },
  { value: 'autograph', label: 'AUTOGRAPH', color: COLORS.purple },
  { value: 'sketch', label: 'SKETCH', color: COLORS.amber },
];

// Phase 1f: Scanner Hints categories
// Each category has a label (shown in dropdown) and a color (shown in hint badges).
const HINT_CATEGORIES = [
  { value: 'visual_cue',      label: 'VISUAL CUE',      color: COLORS.cyan,   description: 'Something to look for on the card face or back (watermarks, frames, effects).' },
  { value: 'numbering_rule',  label: 'NUMBERING RULE',  color: COLORS.gold,   description: 'Serial number format or prefix patterns.' },
  { value: 'disambiguation',  label: 'DISAMBIGUATION',  color: COLORS.green,  description: 'How to distinguish this from something visually similar.' },
  { value: 'exception',       label: 'EXCEPTION',       color: COLORS.amber,  description: 'Known misprints, variants, or oddities.' },
];

function hintCategoryMeta(value) {
  return HINT_CATEGORIES.find(c => c.value === value) || { label: (value || 'UNKNOWN').toUpperCase(), color: COLORS.muted, description: '' };
}

function typeMeta(value) {
  return SUB_SET_TYPES.find(t => t.value === value) || { label: value.toUpperCase(), color: COLORS.muted };
}

// --- Recent-colors localStorage helpers (suggestion #2) ---
const RECENT_COLORS_MAX = 8;
function recentColorsKey(setSlug) {
  return `ck_recent_colors_${setSlug}`;
}
function loadRecentColors(setSlug) {
  if (!setSlug) return [];
  try {
    const raw = localStorage.getItem(recentColorsKey(setSlug));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(c => typeof c === 'string' && /^#[0-9a-fA-F]{6}$/.test(c)).slice(0, RECENT_COLORS_MAX);
  } catch (e) {
    return [];
  }
}
function addRecentColor(setSlug, color) {
  if (!setSlug || !color || !/^#[0-9a-fA-F]{6}$/.test(color)) return [];
  const normalized = color.toLowerCase();
  const existing = loadRecentColors(setSlug).map(c => c.toLowerCase()).filter(c => c !== normalized);
  const next = [normalized, ...existing].slice(0, RECENT_COLORS_MAX);
  try {
    localStorage.setItem(recentColorsKey(setSlug), JSON.stringify(next));
  } catch (e) {
    // localStorage unavailable or full - silent fail
  }
  return next;
}
function clearRecentColors(setSlug) {
  if (!setSlug) return;
  try {
    localStorage.removeItem(recentColorsKey(setSlug));
  } catch (e) {
    // silent fail
  }
}

function generateSlug(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function EditSubSet({ user, setSlug, subSetSlug }) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [parentSet, setParentSet] = useState(null);
  const [subSet, setSubSet] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('insert');
  const [prefix, setPrefix] = useState('');
  const [totalCards, setTotalCards] = useState('');
  const [notes, setNotes] = useState('');

  const [canEdit, setCanEdit] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [formError, setFormError] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  // Parallels state
  const [parallels, setParallels] = useState([]);
  // Inline edit state for existing parallels
  const [editingParallelId, setEditingParallelId] = useState(null);
  const [editPName, setEditPName] = useState('');
  const [editPSlug, setEditPSlug] = useState('');
  const [editPPrintRun, setEditPPrintRun] = useState('');
  const [editPColor, setEditPColor] = useState('#f0c040');
  const [editPOriginalSlug, setEditPOriginalSlug] = useState('');
  const [editPCardCount, setEditPCardCount] = useState(0);
  const [editPSaving, setEditPSaving] = useState(false);
  const [editPError, setEditPError] = useState(null);

  // Phase 1d.3: cards management state
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState(null);
  const [cardsPage, setCardsPage] = useState(1);
  const [savingCardIds, setSavingCardIds] = useState(new Set());
  const [addingCard, setAddingCard] = useState(false);
  const CARDS_PER_PAGE = 25;
  const [showNewParallelForm, setShowNewParallelForm] = useState(false);
  const [pName, setPName] = useState('');
  const [pSlug, setPSlug] = useState('');
  const [pSlugManuallyEdited, setPSlugManuallyEdited] = useState(false);
  const [pPrintRun, setPPrintRun] = useState('');
  const [pColor, setPColor] = useState('#f0c040');
  const [recentColors, setRecentColors] = useState([]);
  const [pSaving, setPSaving] = useState(false);
  const [pError, setPError] = useState(null);

  const [archivingParallelId, setArchivingParallelId] = useState(null);

  // Phase 6C: TraderCracks parallel research (Button 1) state
  const [researchOpen, setResearchOpen] = useState(false);          // Modal visibility
  const [researchLoading, setResearchLoading] = useState(false);    // Function call in flight
  const [researchError, setResearchError] = useState(null);         // Error from function
  const [researchSourceUrl, setResearchSourceUrl] = useState(null); // URL that was fetched
  const [researchNotes, setResearchNotes] = useState('');           // Claude's optional notes
  // proposals: array of { name, slug, print_run, confidence, warnings, checked, suggested_slug_was_null }
  const [researchProposals, setResearchProposals] = useState([]);
  const [researchSaving, setResearchSaving] = useState(false);      // INSERT in flight
  const [researchSummary, setResearchSummary] = useState(null);     // After save: { added, failed[] }

  // Phase 1f: Scanner Hints state
  const [hints, setHints] = useState([]);
  const [showNewHintForm, setShowNewHintForm] = useState(false);
  const [hCategory, setHCategory] = useState(HINT_CATEGORIES[0].value);
  const [hScope, setHScope] = useState('sub_set'); // 'sub_set' | 'parallel'
  const [hParallelId, setHParallelId] = useState('');
  const [hContent, setHContent] = useState('');
  const [hSaving, setHSaving] = useState(false);
  const [hError, setHError] = useState(null);
  const [deletingHintId, setDeletingHintId] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      try {
        const { data: adminRow, error: roleErr } = await supabase
          .from('admin_users')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();
        if (roleErr) throw roleErr;
        if (!mounted) return;
        if (!adminRow) { setRole(null); setLoading(false); return; }
        setRole(adminRow.role);

        const { data: setData, error: setErr } = await supabase
          .from('sets').select('*').eq('slug', setSlug).maybeSingle();
        if (setErr) throw setErr;
        if (!mounted) return;
        if (!setData) { setError(`Set "${setSlug}" not found.`); setLoading(false); return; }
        setParentSet(setData);

        const { data: ssData, error: ssErr } = await supabase
          .from('sub_sets').select('*').eq('set_id', setData.id).eq('slug', subSetSlug).maybeSingle();
        if (ssErr) throw ssErr;
        if (!mounted) return;
        if (!ssData) { setError(`Sub-set "${subSetSlug}" not found in this set.`); setLoading(false); return; }

        setSubSet(ssData);
        setName(ssData.name);
        setType(ssData.type);
        setPrefix(ssData.card_number_prefix || '');
        setTotalCards(ssData.total_cards.toString());
        setNotes(ssData.notes || '');

        // Determine edit permission
        if (adminRow.role === 'owner') {
          setCanEdit(true);
        } else {
          const { data: grant, error: grantErr } = await supabase
            .from('sub_set_editors')
            .select('sub_set_id')
            .eq('sub_set_id', ssData.id)
            .eq('user_id', user.id)
            .maybeSingle();
          if (grantErr) throw grantErr;
          setCanEdit(!!grant);
        }

        await loadParallels(ssData.id, mounted);
        await loadHints(ssData.id, mounted);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load sub-set');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [user.id, setSlug, subSetSlug]);

  // Load recent colors from localStorage when setSlug changes (suggestion #2)
  useEffect(() => {
    setRecentColors(loadRecentColors(setSlug));
  }, [setSlug]);

  // Phase 1d.3: load cards when sub-set changes
  useEffect(() => {
    if (!subSet?.id) return;
    let active = true;
    (async () => {
      setCardsLoading(true);
      setCardsError(null);
      const { data, error } = await supabase
        .from('cards')
        .select('*')
        .eq('sub_set_id', subSet.id)
        .eq('is_archived', false)
        .order('ordering', { ascending: true });
      if (!active) return;
      if (error) {
        setCardsError(error.message);
        setCards([]);
      } else {
        setCards(data || []);
      }
      setCardsLoading(false);
    })();
    return () => { active = false; };
  }, [subSet?.id]);

  async function loadParallels(subSetId, mounted = true) {
    const { data, error: err } = await supabase
      .from('parallels')
      .select('*')
      .eq('sub_set_id', subSetId)
      .eq('is_archived', false)
      .order('ordering', { ascending: true })
      .order('print_run', { ascending: true, nullsFirst: true });
    if (err) throw err;
    if (mounted) setParallels(data || []);
  }

  // Phase 1f: load scanner hints for this sub-set
  async function loadHints(subSetId, mounted = true) {
    const { data, error: err } = await supabase
      .from('scan_hints')
      .select('*')
      .eq('sub_set_id', subSetId)
      .order('ordering', { ascending: true })
      .order('created_at', { ascending: true });
    if (err) throw err;
    if (mounted) setHints(data || []);
  }

  const backToEditSet = () => {
    window.history.pushState({}, '', `/admin/checklists/edit/${setSlug}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  async function refetchSubSet() {
    const { data, error: err } = await supabase
      .from('sub_sets').select('*').eq('id', subSet.id).maybeSingle();
    if (err) throw err;
    setSubSet(data);
    setName(data.name);
    setType(data.type);
    setPrefix(data.card_number_prefix || '');
    setTotalCards(data.total_cards.toString());
    setNotes(data.notes || '');
  }

  async function saveSubSet() {
    setFormError(null);
    setSaveMessage(null);
    if (!name.trim()) { setFormError('Name is required.'); return; }
    if (!type) { setFormError('Type is required.'); return; }
    if (!totalCards || isNaN(parseInt(totalCards)) || parseInt(totalCards) <= 0) {
      setFormError('Total cards must be a positive number.'); return;
    }

    setSaving(true);
    try {
      const { error: updateErr } = await supabase
        .from('sub_sets')
        .update({
          name: name.trim(),
          type,
          card_number_prefix: prefix.trim() || null,
          total_cards: parseInt(totalCards),
          notes: notes.trim() || null,
        })
        .eq('id', subSet.id);

      if (updateErr) { setFormError(updateErr.message || 'Save failed.'); setSaving(false); return; }
      await refetchSubSet();
      setSaveMessage('Saved.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function confirmArchiveSubSet() {
    setFormError(null);
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('sub_sets').update({ is_archived: true }).eq('id', subSet.id);
      if (err) throw err;
      backToEditSet();
    } catch (err) {
      setFormError(err.message || 'Archive failed.');
      setSaving(false);
      setShowArchiveConfirm(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Parallel form handlers
  // ---------------------------------------------------------------------------

  const openNewParallelForm = () => {
    setPName('');
    setPSlug('');
    setPSlugManuallyEdited(false);
    setPPrintRun('');
    setPColor('#f0c040');
    setPError(null);
    setShowNewParallelForm(true);
  };

  const cancelNewParallelForm = () => {
    setShowNewParallelForm(false);
    setPError(null);
  };

  const onPNameChange = (e) => {
    const v = e.target.value;
    setPName(v);
    if (!pSlugManuallyEdited) setPSlug(generateSlug(v));
  };
  const onPSlugChange = (e) => {
    setPSlug(e.target.value);
    setPSlugManuallyEdited(true);
  };

  const saveNewParallel = async () => {
    setPError(null);
    if (!pName.trim()) { setPError('Name is required.'); return; }
    if (!pSlug.trim()) { setPError('Slug is required.'); return; }
    if (!/^[a-z0-9-]+$/.test(pSlug)) {
      setPError('Slug can only contain lowercase letters, numbers, and dashes.'); return;
    }
    let printRunVal = null;
    if (pPrintRun.trim()) {
      const parsed = parseInt(pPrintRun);
      if (isNaN(parsed) || parsed <= 0) {
        setPError('Print run must be a positive number, or leave blank for unnumbered.'); return;
      }
      printRunVal = parsed;
    }
    if (pColor && !/^#[0-9a-fA-F]{6}$/.test(pColor)) {
      setPError('Color must be a 6-digit hex like #f0c040.'); return;
    }

    setPSaving(true);
    try {
      const nextOrdering = (parallels.reduce((max, p) => Math.max(max, p.ordering || 0), 0)) + 10;

      const { error: insertErr } = await supabase
        .from('parallels')
        .insert({
          sub_set_id: subSet.id,
          slug: pSlug.trim(),
          name: pName.trim(),
          print_run: printRunVal,
          display_color: pColor || null,
          ordering: nextOrdering,
        });

      if (insertErr) {
        if (insertErr.code === '23505' || (insertErr.message && insertErr.message.includes('unique'))) {
          setPError(`A parallel with slug "${pSlug}" already exists in this sub-set.`);
        } else {
          setPError(insertErr.message || 'Save failed.');
        }
        setPSaving(false);
        return;
      }

      await loadParallels(subSet.id);

      // Save color to recents after successful insert (suggestion #2)
      if (pColor) {
        const nextRecents = addRecentColor(setSlug, pColor);
        if (nextRecents.length) setRecentColors(nextRecents);
      }

      setShowNewParallelForm(false);
    } catch (err) {
      setPError(err.message || 'Save failed.');
    } finally {
      setPSaving(false);
    }
  };

  // ===========================================================================
  // Phase 6C: TraderCracks parallel research (Button 1)
  // ===========================================================================
  // Workflow:
  //   1. Admin clicks 🔍 RESEARCH PARALLELS button
  //   2. Function fetches TraderCracks page, returns proposed parallels
  //   3. Modal opens showing proposals with editable fields + checkboxes
  //   4. Admin reviews, edits, deselects duplicates/wrong ones
  //   5. Admin clicks ADD SELECTED
  //   6. Each checked proposal is INSERTed via the same path as the manual
  //      + ADD PARALLEL flow (same table, same constraints, same patterns)
  //   7. Parallels list refreshes; modal closes
  // ===========================================================================

  const openResearch = async () => {
    if (!subSet?.id) return;
    setResearchOpen(true);
    setResearchLoading(true);
    setResearchError(null);
    setResearchProposals([]);
    setResearchSourceUrl(null);
    setResearchNotes('');
    setResearchSummary(null);

    try {
      // Get fresh JWT for the function call
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session?.access_token) {
        throw new Error('Could not get auth session — please re-login and try again.');
      }
      const token = sessionData.session.access_token;

      // Call the edge function
      const { data, error } = await supabase.functions.invoke(
        'pipeline-research-parallels-live',
        {
          body: { sub_set_id: subSet.id },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (error) {
        // supabase.functions.invoke wraps non-2xx as error; try to extract body
        let message = error.message || 'Research call failed.';
        try {
          if (error.context && typeof error.context.json === 'function') {
            const body = await error.context.json();
            if (body?.error) message = body.error;
          }
        } catch (_e) { /* ignore */ }
        throw new Error(message);
      }
      if (!data || !data.success) {
        throw new Error(data?.error || 'Function returned unsuccessful response.');
      }

      // Shape proposals for the UI. Each gets a `checked` flag and editable fields.
      const shaped = (data.proposals || []).map((p, idx) => ({
        id: `proposal-${idx}`, // local key, not a DB id
        name: p.name || '',
        slug: p.suggested_slug || '',
        suggested_slug_was_null: p.suggested_slug === null,
        slug_already_exists: !!p.slug_already_exists,
        print_run: p.print_run, // number or null
        confidence: p.confidence || 'low',
        warnings: p.warnings || [],
        // Default checked: yes, UNLESS it's a duplicate or has no slug
        checked: !p.slug_already_exists && !!p.suggested_slug,
      }));

      setResearchProposals(shaped);
      setResearchSourceUrl(data.source_url || null);
      setResearchNotes(data.notes || '');
    } catch (err) {
      setResearchError(err.message || 'Research failed.');
    } finally {
      setResearchLoading(false);
    }
  };

  const closeResearch = () => {
    if (researchSaving) return; // Don't allow closing mid-save
    setResearchOpen(false);
    // Keep error/proposals available for next open? No, clear them.
    setResearchProposals([]);
    setResearchError(null);
    setResearchSourceUrl(null);
    setResearchNotes('');
    setResearchSummary(null);
  };

  const updateProposal = (id, patch) => {
    setResearchProposals(arr => arr.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const toggleProposal = (id) => {
    setResearchProposals(arr => arr.map(p => p.id === id ? { ...p, checked: !p.checked } : p));
  };

  const submitResearch = async () => {
    // Validate: every checked row must have non-empty name and slug
    const checked = researchProposals.filter(p => p.checked);
    if (checked.length === 0) {
      setResearchError('No proposals selected. Check at least one to add, or cancel.');
      return;
    }
    for (const p of checked) {
      if (!p.name?.trim()) {
        setResearchError(`A selected proposal has an empty name — fill it in or uncheck it.`);
        return;
      }
      if (!p.slug?.trim()) {
        setResearchError(`A selected proposal has an empty slug — fill it in or uncheck it.`);
        return;
      }
    }

    setResearchSaving(true);
    setResearchError(null);
    const added = [];
    const failed = [];

    // Insert one at a time so failures don't block the rest
    // Use the same insert pattern as saveNewParallel above
    let nextOrdering = (parallels.reduce((max, p) => Math.max(max, p.ordering || 0), 0)) + 10;

    for (const p of checked) {
      try {
        const { error } = await supabase
          .from('parallels')
          .insert({
            sub_set_id: subSet.id,
            slug: p.slug.trim(),
            name: p.name.trim(),
            print_run: p.print_run, // already number-or-null
            display_color: null, // Admin sets gradient/color later in the existing UI
            ordering: nextOrdering,
          });
        if (error) {
          let msg = error.message || 'Insert failed';
          if (error.code === '23505' || (error.message && error.message.includes('unique'))) {
            msg = `Duplicate slug "${p.slug}" — already exists in this sub-set.`;
          }
          failed.push({ name: p.name, slug: p.slug, error: msg });
        } else {
          added.push({ name: p.name, slug: p.slug, print_run: p.print_run });
          nextOrdering += 10;
        }
      } catch (err) {
        failed.push({ name: p.name, slug: p.slug, error: err.message || 'Insert failed' });
      }
    }

    // Refresh parallels list from DB
    await loadParallels(subSet.id);

    setResearchSummary({ added, failed });
    setResearchSaving(false);

    // If everything succeeded with no failures, auto-close after a brief delay
    if (failed.length === 0 && added.length > 0) {
      setTimeout(() => {
        // Re-check we haven't been re-opened in the meantime
        setResearchOpen(false);
        setResearchProposals([]);
        setResearchSummary(null);
      }, 1500);
    }
  };
  // ===========================================================================
  // End Phase 6C research handlers
  // ===========================================================================

  const beginArchiveParallel = (id) => setArchivingParallelId(id);
  const cancelArchiveParallel = () => setArchivingParallelId(null);

  // Inline edit handlers for existing parallels
  const beginEditParallel = async (par) => {
    setEditingParallelId(par.id);
    setEditPName(par.name || '');
    setEditPSlug(par.slug || '');
    setEditPOriginalSlug(par.slug || '');
    setEditPPrintRun(par.print_run != null ? String(par.print_run) : '');
    setEditPColor(par.display_color || '#f0c040');
    setEditPError(null);
    setEditPCardCount(0);
    try {
      const { count, error } = await supabase
        .from('cards')
        .select('id', { count: 'exact', head: true })
        .eq('set_id', setId)
        .eq('parallel_slug', par.slug);
      if (!error && typeof count === 'number') {
        setEditPCardCount(count);
      }
    } catch (_) { /* non-fatal */ }
  };

  const cancelEditParallel = () => {
    setEditingParallelId(null);
    setEditPName('');
    setEditPSlug('');
    setEditPOriginalSlug('');
    setEditPPrintRun('');
    setEditPColor('#f0c040');
    setEditPError(null);
    setEditPCardCount(0);
  };

  const saveEditParallel = async () => {
    if (!editingParallelId) return;
    setEditPSaving(true);
    setEditPError(null);
    try {
      const trimmedName = (editPName || '').trim();
      const trimmedSlug = (editPSlug || '').trim().toLowerCase();
      if (!trimmedName) { setEditPError('Name is required.'); setEditPSaving(false); return; }
      if (!trimmedSlug) { setEditPError('Slug is required.'); setEditPSaving(false); return; }
      if (!/^[a-z0-9-]+$/.test(trimmedSlug)) {
        setEditPError('Slug can only contain lowercase letters, numbers, and dashes.');
        setEditPSaving(false); return;
      }
      if (trimmedSlug !== editPOriginalSlug) {
        const { data: existing, error: checkErr } = await supabase
          .from('parallels')
          .select('id')
          .eq('sub_set_id', subSetId)
          .eq('slug', trimmedSlug)
          .neq('id', editingParallelId)
          .maybeSingle();
        if (checkErr) throw checkErr;
        if (existing) {
          setEditPError(`A parallel with slug "${trimmedSlug}" already exists in this sub-set.`);
          setEditPSaving(false); return;
        }
      }
      const printRunNum = editPPrintRun === '' || editPPrintRun == null ? null : parseInt(editPPrintRun, 10);
      if (editPPrintRun !== '' && (isNaN(printRunNum) || printRunNum < 1)) {
        setEditPError('Print run must be a positive whole number, or blank.');
        setEditPSaving(false); return;
      }
      const { error: updateErr } = await supabase
        .from('parallels')
        .update({
          name: trimmedName,
          slug: trimmedSlug,
          print_run: printRunNum,
          display_color: editPColor || null,
        })
        .eq('id', editingParallelId);
      if (updateErr) throw updateErr;
      setParallels(prev => prev.map(p =>
        p.id === editingParallelId
          ? { ...p, name: trimmedName, slug: trimmedSlug, print_run: printRunNum, display_color: editPColor || null }
          : p
      ));
      cancelEditParallel();
    } catch (err) {
      setEditPError(err.message || 'Failed to save parallel.');
      setEditPSaving(false);
    }
  };

  const confirmArchiveParallel = async (id) => {
    try {
      const { error: err } = await supabase
        .from('parallels').update({ is_archived: true }).eq('id', id);
      if (err) throw err;
      await loadParallels(subSet.id);
      setArchivingParallelId(null);
    } catch (err) {
      alert('Archive failed: ' + (err.message || 'unknown'));
      setArchivingParallelId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Phase 1f: Scanner Hint form handlers
  // ---------------------------------------------------------------------------

  const openNewHintForm = () => {
    setHCategory(HINT_CATEGORIES[0].value);
    setHScope('sub_set');
    setHParallelId('');
    setHContent('');
    setHError(null);
    setShowNewHintForm(true);
  };

  const cancelNewHintForm = () => {
    setShowNewHintForm(false);
    setHError(null);
  };

  const saveNewHint = async () => {
    setHError(null);
    if (!hCategory) { setHError('Category is required.'); return; }
    if (!hContent.trim()) { setHError('Content is required.'); return; }
    if (hScope === 'parallel' && !hParallelId) {
      setHError('Select a parallel, or change scope to sub-set.'); return;
    }

    setHSaving(true);
    try {
      const nextOrdering = (hints.reduce((max, h) => Math.max(max, h.ordering || 0), 0)) + 10;

      const { error: insertErr } = await supabase
        .from('scan_hints')
        .insert({
          set_id: parentSet.id,
          sub_set_id: subSet.id,
          parallel_id: hScope === 'parallel' ? hParallelId : null,
          hint_category: hCategory,
          content: hContent.trim(),
          ordering: nextOrdering,
        });

      if (insertErr) {
        setHError(insertErr.message || 'Save failed.');
        setHSaving(false);
        return;
      }

      await loadHints(subSet.id);
      setShowNewHintForm(false);
    } catch (err) {
      setHError(err.message || 'Save failed.');
    } finally {
      setHSaving(false);
    }
  };

  const beginDeleteHint = (id) => setDeletingHintId(id);
  const cancelDeleteHint = () => setDeletingHintId(null);
  const confirmDeleteHint = async (id) => {
    try {
      const { error: err } = await supabase
        .from('scan_hints').delete().eq('id', id);
      if (err) throw err;
      await loadHints(subSet.id);
      setDeletingHintId(null);
    } catch (err) {
      alert('Delete failed: ' + (err.message || 'unknown'));
      setDeletingHintId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace', fontSize: 13 }}>
          Loading sub-set...
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>EDIT SUB-SET</div>
          <div style={{ color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 16, marginTop: 24 }}>
            You don't have admin access.
          </div>
          <button onClick={backToEditSet} style={{ ...buttonOutlineStyle, marginTop: 20 }}>← BACK</button>
        </div>
      </div>
    );
  }

  if (error || !subSet) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>EDIT SUB-SET</div>
          <div style={{ color: COLORS.red, fontFamily: 'Share Tech Mono, monospace', fontSize: 13, marginTop: 24 }}>
            {error || 'Sub-set not found.'}
          </div>
          <button onClick={backToEditSet} style={{ ...buttonOutlineStyle, marginTop: 20 }}>← BACK</button>
        </div>
      </div>
    );
  }

  const meta = typeMeta(subSet.type);

  // Phase 1d.3: card mutation handlers
  const markSaving = (cardId, saving) => {
    setSavingCardIds(prev => {
      const next = new Set(prev);
      if (saving) next.add(cardId); else next.delete(cardId);
      return next;
    });
  };

  const handleCardFieldChange = (cardId, field, value) => {
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, [field]: value } : c));
  };

  const handleCardFieldBlur = async (card, field) => {
    const currentValue = card[field];
    // Validate required fields
    if (field === 'card_number' && (!currentValue || !String(currentValue).trim())) {
      alert('Card number cannot be empty.');
      // Reload to revert
      const { data } = await supabase.from('cards').select('*').eq('id', card.id).maybeSingle();
      if (data) setCards(prev => prev.map(c => c.id === card.id ? data : c));
      return;
    }
    if (field === 'name' && (!currentValue || !String(currentValue).trim())) {
      alert('Card name cannot be empty.');
      const { data } = await supabase.from('cards').select('*').eq('id', card.id).maybeSingle();
      if (data) setCards(prev => prev.map(c => c.id === card.id ? data : c));
      return;
    }

    const payload = {};
    payload[field] = currentValue && String(currentValue).trim()
      ? String(currentValue).trim()
      : (field === 'artist' ? null : currentValue);

    markSaving(card.id, true);
    const { error } = await supabase.from('cards').update(payload).eq('id', card.id);
    markSaving(card.id, false);

    if (error) {
      alert(`Failed to save: ${error.message}`);
    }
  };

  const handleAddCard = async () => {
    if (!subSet?.id) return;
    setAddingCard(true);
    // Compute next ordering value
    const maxOrdering = cards.length > 0
      ? Math.max(...cards.map(c => c.ordering || 0))
      : 0;
    const nextOrdering = maxOrdering + 10;

    const { data, error } = await supabase.from('cards').insert({
      sub_set_id: subSet.id,
      card_number: '',
      name: 'New Card',
      artist: null,
      ordering: nextOrdering,
    }).select('*').maybeSingle();

    setAddingCard(false);

    if (error || !data) {
      alert(`Failed to add card: ${error?.message || 'unknown error'}`);
      return;
    }

    setCards(prev => [...prev, data]);
    // Navigate to the last page so the new row is visible
    const newTotal = cards.length + 1;
    const newLastPage = Math.max(1, Math.ceil(newTotal / CARDS_PER_PAGE));
    setCardsPage(newLastPage);
  };

  const handleDeleteCard = async (card) => {
    if (!window.confirm(`Delete card "${card.card_number} — ${card.name}"?\n\nThis cannot be undone.`)) return;
    const { error } = await supabase.from('cards').delete().eq('id', card.id);
    if (error) {
      alert(`Delete failed: ${error.message}`);
      return;
    }
    setCards(prev => prev.filter(c => c.id !== card.id));
  };

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={headerTitle}>EDIT SUB-SET</div>
          <div style={{
            fontFamily: 'Share Tech Mono, monospace',
            color: COLORS.green, fontSize: 11, letterSpacing: 2,
          }}>
            {(typeof window !== 'undefined' && window.location.hostname === 'collectionkeeper.org') ? 'PRODUCTION' : 'STAGING'} · {role.toUpperCase()}
          </div>
        </div>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted,
          fontSize: 12, marginBottom: 20,
        }}>
          <span style={{ color: COLORS.cyan }}>{parentSet.slug}</span>
          <span style={{ margin: '0 6px' }}>/</span>
          <span style={{ color: COLORS.cyan }}>{subSet.slug}</span>
        </div>

        <div style={{ marginBottom: 20 }}>
          <button onClick={backToEditSet} style={buttonOutlineStyle}>← BACK TO SET</button>
        </div>

        {/* Sub-set fields card */}
        <div style={{
          background: COLORS.cardBg, borderRadius: 6, padding: 20,
          border: `1px solid ${COLORS.muted}22`, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
            <span style={{
              color: meta.color, border: `1px solid ${meta.color}`,
              background: `${meta.color}15`, padding: '3px 10px', borderRadius: 3,
              fontFamily: 'Share Tech Mono, monospace', fontSize: 10, letterSpacing: 1.5,
            }}>{meta.label}</span>
            {saveMessage && (
              <span style={{
                color: COLORS.green, fontSize: 12,
                fontFamily: 'Share Tech Mono, monospace', marginLeft: 8,
              }}>
                ✓ {saveMessage}
              </span>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={formLabelStyle}>SLUG (READ-ONLY)</label>
            <input
              type="text" value={subSet.slug} disabled
              style={{ ...formInputStyle, color: COLORS.muted, cursor: 'not-allowed', background: '#020408' }}
            />
            <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 11, marginTop: 4 }}>
              Slug cannot be changed after creation.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={formLabelStyle}>NAME</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                style={formInputStyle} disabled={!canEdit || saving}
              />
            </div>
            <div>
              <label style={formLabelStyle}>TYPE</label>
              <select
                value={type} onChange={(e) => setType(e.target.value)}
                style={formInputStyle} disabled={!canEdit || saving}
              >
                {SUB_SET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={formLabelStyle}>PREFIX</label>
              <input
                type="text" value={prefix} onChange={(e) => setPrefix(e.target.value)}
                style={formInputStyle} placeholder="e.g. POW-" disabled={!canEdit || saving}
              />
            </div>
            <div>
              <label style={formLabelStyle}>TOTAL CARDS</label>
              <input
                type="number" value={totalCards} onChange={(e) => setTotalCards(e.target.value)}
                style={formInputStyle} disabled={!canEdit || saving}
              />
            </div>
            <div />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={formLabelStyle}>NOTES</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              style={{ ...formInputStyle, minHeight: 60, resize: 'vertical', fontFamily: 'Rajdhani, sans-serif', fontSize: 14 }}
              placeholder="Optional freeform notes for admins."
              disabled={!canEdit || saving}
            />
          </div>

          {formError && (
            <div style={{
              color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
              padding: 8, marginTop: 8, marginBottom: 8,
              background: `${COLORS.red}15`, borderRadius: 4,
            }}>
              {formError}
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button
              onClick={saveSubSet} disabled={!canEdit || saving}
              style={{ ...buttonPrimaryStyle, opacity: (!canEdit || saving) ? 0.6 : 1, cursor: (!canEdit || saving) ? 'not-allowed' : 'pointer' }}
            >
              {saving ? 'SAVING...' : 'SAVE'}
            </button>
            {role === 'owner' && (
              !showArchiveConfirm ? (
                <button
                  onClick={() => setShowArchiveConfirm(true)} disabled={saving}
                  style={{ ...buttonOutlineStyle, color: COLORS.red, borderColor: COLORS.red, marginLeft: 'auto' }}
                >
                  ARCHIVE SUB-SET
                </button>
              ) : (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button
                    onClick={confirmArchiveSubSet} disabled={saving}
                    style={{ ...buttonPrimaryStyle, background: COLORS.red, color: '#fff', fontSize: 11, padding: '8px 12px' }}
                  >
                    {saving ? 'ARCHIVING...' : 'CONFIRM'}
                  </button>
                  <button
                    onClick={() => setShowArchiveConfirm(false)} disabled={saving}
                    style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted, fontSize: 11, padding: '8px 12px' }}
                  >
                    CANCEL
                  </button>
                </div>
              )
            )}
          </div>

          {!canEdit && (
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              color: COLORS.muted, fontSize: 11, marginTop: 10,
            }}>
              You don't have edit access to this sub-set. Contact the owner.
            </div>
          )}
        </div>

        {/* Parallels section */}
        <div style={{
          background: COLORS.cardBg, borderRadius: 6, padding: 20,
          border: `1px solid ${COLORS.muted}22`, marginBottom: 16,
        }}>
          <div style={{
            fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
            fontSize: 12, letterSpacing: 1.5, marginBottom: 4,
          }}>
            PARALLELS
          </div>
          <div style={{ fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 12, marginBottom: 14 }}>
            Parallel variants available for cards in this sub-set (e.g. Orange /25, Red Refractor /5).
          </div>

          {canEdit && !showNewParallelForm && (
            <div style={{ marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={openNewParallelForm} style={buttonPrimaryStyle}>+ ADD PARALLEL</button>
              <button
                onClick={openResearch}
                disabled={researchOpen}
                style={{
                  ...buttonOutlineStyle,
                  borderColor: COLORS.cyan,
                  color: COLORS.cyan,
                  opacity: researchOpen ? 0.5 : 1,
                  cursor: researchOpen ? 'not-allowed' : 'pointer',
                }}
                title="Use AI to fetch parallels from TraderCracks for this sub-set"
              >
                🔍 RESEARCH PARALLELS
              </button>
            </div>
          )}

          {showNewParallelForm && (
            <div style={{
              border: `1px solid ${COLORS.cyan}`, borderRadius: 4,
              padding: 14, marginBottom: 14, background: '#020408',
            }}>
              <div style={{
                fontFamily: 'Orbitron, sans-serif', color: COLORS.cyan,
                fontSize: 11, letterSpacing: 1.5, marginBottom: 10,
              }}>
                NEW PARALLEL
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={formLabelStyle}>NAME</label>
                  <input
                    type="text" value={pName} onChange={onPNameChange}
                    style={formInputStyle} placeholder="e.g. Orange Refractor" autoFocus
                  />
                </div>
                <div>
                  <label style={formLabelStyle}>SLUG</label>
                  <input
                    type="text" value={pSlug} onChange={onPSlugChange}
                    style={formInputStyle} placeholder="e.g. orange-refractor"
                  />
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                    {pSlugManuallyEdited ? 'Manually edited.' : 'Auto-generated.'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '120px 160px 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={formLabelStyle}>PRINT RUN</label>
                  <input
                    type="number" value={pPrintRun} onChange={(e) => setPPrintRun(e.target.value)}
                    style={formInputStyle} placeholder="25"
                  />
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                    Leave blank for unnumbered.
                  </div>
                </div>
                <div>
                  <label style={formLabelStyle}>DISPLAY COLOR</label>
                  {recentColors.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, letterSpacing: 1, marginRight: 4 }}>
                        RECENT:
                      </span>
                      {recentColors.map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setPColor(c)}
                          title={c}
                          style={{
                            width: 24, height: 24, borderRadius: 4,
                            border: pColor.toLowerCase() === c.toLowerCase() ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.muted}66`,
                            background: c, cursor: 'pointer', padding: 0,
                          }}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => { clearRecentColors(setSlug); setRecentColors([]); }}
                        style={{
                          marginLeft: 'auto', background: 'transparent', border: 'none',
                          color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace',
                          fontSize: 10, letterSpacing: 1, cursor: 'pointer', padding: '2px 4px',
                        }}
                      >
                        CLEAR
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="color" value={pColor} onChange={(e) => setPColor(e.target.value)}
                      style={{ width: 36, height: 36, padding: 0, border: `1px solid ${COLORS.muted}66`, borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
                    />
                    <input
                      type="text" value={pColor} onChange={(e) => setPColor(e.target.value)}
                      style={{ ...formInputStyle, flex: 1 }} placeholder="#f0c040"
                    />
                  </div>
                </div>
                <div />
              </div>

              {pError && (
                <div style={{
                  color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                  padding: 8, marginBottom: 10, background: `${COLORS.red}15`, borderRadius: 4,
                }}>
                  {pError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={saveNewParallel} disabled={pSaving}
                  style={{ ...buttonPrimaryStyle, opacity: pSaving ? 0.6 : 1, cursor: pSaving ? 'not-allowed' : 'pointer' }}
                >
                  {pSaving ? 'SAVING...' : 'SAVE PARALLEL'}
                </button>
                <button
                  onClick={cancelNewParallelForm} disabled={pSaving}
                  style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          )}

          <div style={{ border: `1px solid ${COLORS.muted}22`, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 100px 50px 220px',
              padding: '8px 12px', background: COLORS.bg,
              fontFamily: 'Orbitron, sans-serif', fontSize: 10, color: COLORS.gold, letterSpacing: 1,
            }}>
              <div>NAME</div>
              <div>PRINT RUN</div>
              <div>COLOR</div>
              <div>ACTIONS</div>
            </div>

            {parallels.length === 0 ? (
              <div style={{
                padding: '24px 12px', textAlign: 'center',
                fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 13,
              }}>
                No parallels yet. Click <span style={{ color: COLORS.cyan }}>+ ADD PARALLEL</span> to create one.
              </div>
            ) : (
              parallels.map(p => (
                <React.Fragment key={p.id}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '2fr 100px 50px 220px',
                  padding: '10px 12px', borderTop: `1px solid ${COLORS.muted}22`,
                  fontFamily: 'Rajdhani, sans-serif', fontSize: 13, alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace' }}>{p.slug}</div>
                  </div>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 12 }}>
                    {p.print_run ? `/${p.print_run}` : <span style={{ color: COLORS.muted }}>—</span>}
                  </div>
                  <div>
                    {p.display_color && (
                      <span title={p.display_color} style={{
                        display: 'inline-block', width: 24, height: 24,
                        background: p.display_color, borderRadius: 4,
                        border: `1px solid ${COLORS.muted}44`,
                      }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {canEdit && editingParallelId !== p.id && archivingParallelId !== p.id && (
                      <>
                        <button
                          onClick={() => beginEditParallel(p)}
                          style={{ ...buttonOutlineStyle, padding: '3px 8px', fontSize: 10, color: COLORS.cyan, borderColor: COLORS.cyan }}
                        >EDIT</button>
                        <button
                          onClick={() => beginArchiveParallel(p.id)}
                          style={{ ...buttonOutlineStyle, padding: '3px 8px', fontSize: 10, color: COLORS.red, borderColor: COLORS.red }}
                        >ARCHIVE</button>
                      </>
                    )}
                    {canEdit && archivingParallelId === p.id && (
                      <>
                        <button
                          onClick={() => confirmArchiveParallel(p.id)}
                          style={{ padding: '3px 6px', fontSize: 10, background: COLORS.red, color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, cursor: 'pointer' }}
                        >CONFIRM</button>
                        <button
                          onClick={cancelArchiveParallel}
                          style={{ padding: '3px 6px', fontSize: 10, background: 'transparent', color: COLORS.muted, border: `1px solid ${COLORS.muted}`, borderRadius: 4, fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, cursor: 'pointer' }}
                        >✕</button>
                      </>
                    )}
                  </div>
                </div>
                {editingParallelId === p.id && (
                  <div style={{
                    border: `1px solid ${COLORS.cyan}`, borderRadius: 4,
                    padding: 14, margin: '4px 12px 14px 12px', background: '#020408',
                  }}>
                    <div style={{
                      fontFamily: 'Orbitron, sans-serif', color: COLORS.cyan,
                      fontSize: 11, letterSpacing: 1.5, marginBottom: 10,
                    }}>EDIT PARALLEL</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={formLabelStyle}>NAME</label>
                        <input type="text" value={editPName} onChange={(e) => setEditPName(e.target.value)} style={formInputStyle} autoFocus />
                      </div>
                      <div>
                        <label style={formLabelStyle}>SLUG</label>
                        <input type="text" value={editPSlug} onChange={(e) => setEditPSlug(e.target.value)} style={formInputStyle} />
                        {editPSlug !== editPOriginalSlug && editPCardCount > 0 && (
                          <div style={{
                            color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 12,
                            marginTop: 6, padding: 8, background: `${COLORS.red}15`, borderRadius: 4,
                          }}>
                            ⚠️ WARNING: {editPCardCount} {editPCardCount === 1 ? 'card is' : 'cards are'} currently using this slug and will be impacted
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 220px 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={formLabelStyle}>PRINT RUN</label>
                        <input type="number" value={editPPrintRun} onChange={(e) => setEditPPrintRun(e.target.value)} style={formInputStyle} placeholder="25" />
                        <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                          Leave blank for unnumbered.
                        </div>
                      </div>
                      <div>
                        <label style={formLabelStyle}>DISPLAY COLOR</label>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input type="color" value={editPColor} onChange={(e) => setEditPColor(e.target.value)} style={{ width: 36, height: 36, padding: 0, border: `1px solid ${COLORS.muted}66`, borderRadius: 4, cursor: 'pointer', background: 'transparent' }} />
                          <input type="text" value={editPColor} onChange={(e) => setEditPColor(e.target.value)} style={{ ...formInputStyle, flex: 1 }} />
                        </div>
                      </div>
                      <div />
                    </div>
                    {editPError && (
                      <div style={{
                        color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                        padding: 8, marginBottom: 10, background: `${COLORS.red}15`, borderRadius: 4,
                      }}>{editPError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={saveEditParallel} disabled={editPSaving}
                        style={{ ...buttonPrimaryStyle, opacity: editPSaving ? 0.6 : 1, cursor: editPSaving ? 'not-allowed' : 'pointer' }}>
                        {editPSaving ? 'SAVING...' : 'SAVE CHANGES'}
                      </button>
                      <button onClick={cancelEditParallel} disabled={editPSaving}
                        style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}>CANCEL</button>
                    </div>
                  </div>
                )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        {/* Phase 1f: Scanner Hints section */}
        <div style={{
          background: COLORS.cardBg, borderRadius: 6, padding: 20,
          border: `1px solid ${COLORS.muted}22`, marginBottom: 16,
        }}>
          <div style={{
            fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
            fontSize: 12, letterSpacing: 1.5, marginBottom: 4,
          }}>
            SCANNER HINTS
          </div>
          <div style={{ fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 12, marginBottom: 14 }}>
            Rules the AI scanner uses to identify cards in this sub-set (visual cues, numbering patterns, disambiguation tips, exceptions).
          </div>

          {canEdit && !showNewHintForm && (
            <div style={{ marginBottom: 14 }}>
              <button onClick={openNewHintForm} style={buttonPrimaryStyle}>+ ADD HINT</button>
            </div>
          )}

          {showNewHintForm && (
            <div style={{
              border: `1px solid ${COLORS.cyan}`, borderRadius: 4,
              padding: 14, marginBottom: 14, background: '#020408',
            }}>
              <div style={{
                fontFamily: 'Orbitron, sans-serif', color: COLORS.cyan,
                fontSize: 11, letterSpacing: 1.5, marginBottom: 10,
              }}>
                NEW SCANNER HINT
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={formLabelStyle}>CATEGORY</label>
                  <select
                    value={hCategory}
                    onChange={(e) => setHCategory(e.target.value)}
                    style={formInputStyle}
                  >
                    {HINT_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                    {hintCategoryMeta(hCategory).description}
                  </div>
                </div>
                <div>
                  <label style={formLabelStyle}>SCOPE</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Rajdhani, sans-serif', fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="radio" name="hScope" value="sub_set"
                        checked={hScope === 'sub_set'}
                        onChange={() => { setHScope('sub_set'); setHParallelId(''); }}
                      />
                      Apply to all cards in this sub-set
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Rajdhani, sans-serif', fontSize: 13, cursor: parallels.length === 0 ? 'not-allowed' : 'pointer', opacity: parallels.length === 0 ? 0.5 : 1 }}>
                      <input
                        type="radio" name="hScope" value="parallel"
                        checked={hScope === 'parallel'}
                        onChange={() => setHScope('parallel')}
                        disabled={parallels.length === 0}
                      />
                      Apply to specific parallel
                    </label>
                  </div>
                </div>
              </div>

              {hScope === 'parallel' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={formLabelStyle}>PARALLEL</label>
                  <select
                    value={hParallelId}
                    onChange={(e) => setHParallelId(e.target.value)}
                    style={formInputStyle}
                  >
                    <option value="">— Choose a parallel —</option>
                    {parallels.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.print_run ? ` /${p.print_run}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 10 }}>
                <label style={formLabelStyle}>CONTENT</label>
                <textarea
                  value={hContent}
                  onChange={(e) => setHContent(e.target.value)}
                  style={{ ...formInputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'Rajdhani, sans-serif', fontSize: 14 }}
                  placeholder="e.g. Red Logofractor has a diagonal Star Wars watermark on front"
                  autoFocus
                />
              </div>

              {hError && (
                <div style={{
                  color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                  padding: 8, marginBottom: 10, background: `${COLORS.red}15`, borderRadius: 4,
                }}>
                  {hError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={saveNewHint} disabled={hSaving}
                  style={{ ...buttonPrimaryStyle, opacity: hSaving ? 0.6 : 1, cursor: hSaving ? 'not-allowed' : 'pointer' }}
                >
                  {hSaving ? 'SAVING...' : 'SAVE HINT'}
                </button>
                <button
                  onClick={cancelNewHintForm} disabled={hSaving}
                  style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          )}

          <div style={{ border: `1px solid ${COLORS.muted}22`, borderRadius: 4, overflow: 'hidden' }}>
            {hints.length === 0 ? (
              <div style={{
                padding: '24px 12px', textAlign: 'center',
                fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 13,
              }}>
                No hints yet. Click <span style={{ color: COLORS.cyan }}>+ ADD HINT</span> to create one.
              </div>
            ) : (
              hints.map(h => {
                const cm = hintCategoryMeta(h.hint_category);
                const parallel = h.parallel_id ? parallels.find(p => p.id === h.parallel_id) : null;
                const scopeLabel = parallel
                  ? `PARALLEL · ${parallel.name.toUpperCase()}`
                  : 'SUB-SET WIDE';
                return (
                  <div key={h.id} style={{
                    padding: '12px 14px', borderBottom: `1px solid ${COLORS.muted}22`,
                    fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                  }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        color: cm.color, border: `1px solid ${cm.color}`,
                        background: `${cm.color}15`, padding: '2px 8px', borderRadius: 3,
                        fontFamily: 'Share Tech Mono, monospace', fontSize: 10, letterSpacing: 1.2,
                      }}>{cm.label}</span>
                      <span style={{
                        color: parallel ? COLORS.purple : COLORS.muted,
                        fontFamily: 'Share Tech Mono, monospace', fontSize: 10, letterSpacing: 1.2,
                      }}>{scopeLabel}</span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                        {canEdit && deletingHintId !== h.id && (
                          <button
                            onClick={() => beginDeleteHint(h.id)}
                            style={{ ...buttonOutlineStyle, padding: '3px 8px', fontSize: 10, color: COLORS.red, borderColor: COLORS.red }}
                          >
                            DELETE
                          </button>
                        )}
                        {canEdit && deletingHintId === h.id && (
                          <>
                            <button
                              onClick={() => confirmDeleteHint(h.id)}
                              style={{ padding: '3px 6px', fontSize: 10, background: COLORS.red, color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, cursor: 'pointer' }}
                            >
                              CONFIRM
                            </button>
                            <button
                              onClick={cancelDeleteHint}
                              style={{ padding: '3px 6px', fontSize: 10, background: 'transparent', color: COLORS.muted, border: `1px solid ${COLORS.muted}`, borderRadius: 4, fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ color: '#fff', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                      {h.content}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Phase 1d.3: Cards management section */}
      <div style={{
        marginTop: 20, background: COLORS.panel,
        border: `1px solid ${COLORS.muted}33`, borderRadius: 6, padding: 16,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14, paddingBottom: 10,
          borderBottom: `1px solid ${COLORS.muted}22`,
        }}>
          <div>
            <div style={{
              fontFamily: 'Orbitron, sans-serif', fontSize: 13,
              color: COLORS.gold, letterSpacing: 2, marginBottom: 2,
            }}>
              CARDS
            </div>
            <div style={{
              fontFamily: 'Share Tech Mono, monospace', fontSize: 11,
              color: COLORS.muted,
            }}>
              {cardsLoading ? 'Loading...' : `${cards.length} card${cards.length === 1 ? '' : 's'}`}
            </div>
          </div>
          <button
            onClick={handleAddCard}
            disabled={addingCard || cardsLoading}
            style={{
              padding: '6px 14px',
              fontFamily: 'Orbitron, sans-serif', fontSize: 11, letterSpacing: 1,
              background: COLORS.cyan, color: COLORS.bg,
              border: 'none', borderRadius: 4, cursor: 'pointer',
              opacity: (addingCard || cardsLoading) ? 0.5 : 1,
            }}
          >
            {addingCard ? 'ADDING...' : '+ ADD CARD'}
          </button>
        </div>

        {cardsError && (
          <div style={{
            padding: 10, background: `${COLORS.red}15`, borderRadius: 4,
            color: COLORS.red, fontFamily: 'Share Tech Mono, monospace',
            fontSize: 11, marginBottom: 10,
          }}>
            Error: {cardsError}
          </div>
        )}

        {!cardsLoading && cards.length === 0 && !cardsError && (
          <div style={{
            padding: 20, textAlign: 'center',
            fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 13,
          }}>
            No cards yet. Click + ADD CARD to create one.
          </div>
        )}

        {cards.length > 0 && (
          <>
            {/* Column headers */}
            <div style={{
              display: 'grid', gridTemplateColumns: '90px 1fr 1fr 30px',
              gap: 8, paddingBottom: 6, paddingLeft: 4,
              borderBottom: `1px solid ${COLORS.muted}22`,
              fontFamily: 'Orbitron, sans-serif', fontSize: 9,
              color: COLORS.gold, letterSpacing: 1,
            }}>
              <div>CARD #</div>
              <div>NAME</div>
              <div>ARTIST</div>
              <div></div>
            </div>

            {/* Cards (paginated) */}
            {cards
              .slice((cardsPage - 1) * CARDS_PER_PAGE, cardsPage * CARDS_PER_PAGE)
              .map(card => {
                const isSaving = savingCardIds.has(card.id);
                return (
                  <div key={card.id} style={{
                    display: 'grid', gridTemplateColumns: '90px 1fr 1fr 30px',
                    gap: 8, padding: '5px 0', alignItems: 'center',
                    borderBottom: `1px solid ${COLORS.muted}15`,
                    opacity: isSaving ? 0.7 : 1,
                  }}>
                    <input
                      type="text" value={card.card_number || ''}
                      onChange={(e) => handleCardFieldChange(card.id, 'card_number', e.target.value)}
                      onBlur={() => handleCardFieldBlur(card, 'card_number')}
                      disabled={isSaving}
                      style={{
                        padding: '5px 7px', fontSize: 11,
                        fontFamily: 'Share Tech Mono, monospace',
                        background: '#020408', color: '#fff',
                        border: `1px solid ${COLORS.muted}33`, borderRadius: 3,
                      }}
                    />
                    <input
                      type="text" value={card.name || ''}
                      onChange={(e) => handleCardFieldChange(card.id, 'name', e.target.value)}
                      onBlur={() => handleCardFieldBlur(card, 'name')}
                      disabled={isSaving}
                      style={{
                        padding: '5px 7px', fontSize: 11,
                        fontFamily: 'Rajdhani, sans-serif',
                        background: '#020408', color: '#fff',
                        border: `1px solid ${COLORS.muted}33`, borderRadius: 3,
                      }}
                    />
                    <input
                      type="text" value={card.artist || ''}
                      onChange={(e) => handleCardFieldChange(card.id, 'artist', e.target.value)}
                      onBlur={() => handleCardFieldBlur(card, 'artist')}
                      disabled={isSaving}
                      placeholder="—"
                      style={{
                        padding: '5px 7px', fontSize: 11,
                        fontFamily: 'Rajdhani, sans-serif',
                        background: '#020408', color: '#fff',
                        border: `1px solid ${COLORS.muted}33`, borderRadius: 3,
                      }}
                    />
                    <div
                      onClick={() => !isSaving && handleDeleteCard(card)}
                      style={{
                        textAlign: 'center', color: COLORS.red,
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontFamily: 'Share Tech Mono, monospace', fontSize: 14,
                        opacity: isSaving ? 0.5 : 1,
                      }}
                      title={isSaving ? 'Saving…' : 'Delete card'}
                    >
                      ✕
                    </div>
                  </div>
                );
              })}

            {/* Pagination */}
            {cards.length > CARDS_PER_PAGE && (
              <div style={{
                marginTop: 14, paddingTop: 10,
                borderTop: `1px solid ${COLORS.muted}22`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontFamily: 'Share Tech Mono, monospace', fontSize: 11,
                color: COLORS.muted,
              }}>
                <div>
                  Page {cardsPage} of {Math.ceil(cards.length / CARDS_PER_PAGE)} · showing {((cardsPage - 1) * CARDS_PER_PAGE) + 1}–{Math.min(cardsPage * CARDS_PER_PAGE, cards.length)} of {cards.length}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setCardsPage(p => Math.max(1, p - 1))}
                    disabled={cardsPage === 1}
                    style={{
                      padding: '4px 10px', fontSize: 10,
                      background: 'transparent', color: COLORS.muted,
                      border: `1px solid ${COLORS.muted}`, borderRadius: 3,
                      cursor: cardsPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: cardsPage === 1 ? 0.4 : 1,
                      fontFamily: 'Orbitron, sans-serif', letterSpacing: 1,
                    }}
                  >
                    ◀ PREV
                  </button>
                  <button
                    onClick={() => setCardsPage(p => Math.min(Math.ceil(cards.length / CARDS_PER_PAGE), p + 1))}
                    disabled={cardsPage >= Math.ceil(cards.length / CARDS_PER_PAGE)}
                    style={{
                      padding: '4px 10px', fontSize: 10,
                      background: 'transparent', color: COLORS.muted,
                      border: `1px solid ${COLORS.muted}`, borderRadius: 3,
                      cursor: cardsPage >= Math.ceil(cards.length / CARDS_PER_PAGE) ? 'not-allowed' : 'pointer',
                      opacity: cardsPage >= Math.ceil(cards.length / CARDS_PER_PAGE) ? 0.4 : 1,
                      fontFamily: 'Orbitron, sans-serif', letterSpacing: 1,
                    }}
                  >
                    NEXT ▶
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Phase 6C: Research Parallels modal */}
      {researchOpen && (
        <div
          onClick={closeResearch}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COLORS.cardBg,
              border: `1px solid ${COLORS.cyan}`,
              borderRadius: 6,
              padding: 24,
              maxWidth: 880, width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              fontFamily: 'Rajdhani, sans-serif',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 8, gap: 16,
            }}>
              <div>
                <div style={{
                  fontFamily: 'Orbitron, sans-serif',
                  color: COLORS.cyan, fontSize: 14, letterSpacing: 2, fontWeight: 700,
                }}>
                  🔍 RESEARCH PARALLELS
                </div>
                <div style={{ color: COLORS.muted, fontSize: 12, marginTop: 4 }}>
                  {subSet?.name || ''} · {parentSet?.name || ''}
                </div>
              </div>
              <button
                onClick={closeResearch}
                disabled={researchSaving}
                style={{
                  background: 'transparent', border: 'none',
                  color: COLORS.muted, fontSize: 24, cursor: researchSaving ? 'not-allowed' : 'pointer',
                  padding: 0, lineHeight: 1,
                }}
                title="Close"
              >×</button>
            </div>

            {/* Source URL */}
            {researchSourceUrl && (
              <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 12, wordBreak: 'break-all' }}>
                Source: <a href={researchSourceUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.cyan }}>{researchSourceUrl}</a>
              </div>
            )}

            {/* Notes from Claude */}
            {researchNotes && (
              <div style={{
                background: COLORS.bg, border: `1px solid ${COLORS.muted}66`,
                borderRadius: 4, padding: 10, marginBottom: 14,
                fontSize: 12, color: COLORS.muted,
              }}>
                <span style={{ color: COLORS.cyan, fontWeight: 700 }}>Note:</span> {researchNotes}
              </div>
            )}

            {/* Loading state */}
            {researchLoading && (
              <div style={{ color: COLORS.muted, padding: 40, textAlign: 'center', fontFamily: 'Share Tech Mono, monospace' }}>
                Fetching from TraderCracks and analyzing parallels...
                <div style={{ fontSize: 11, marginTop: 8 }}>This typically takes 30-60 seconds.</div>
              </div>
            )}

            {/* Error state */}
            {!researchLoading && researchError && (
              <div style={{
                background: '#2a0808', border: `1px solid ${COLORS.red}`, color: COLORS.red,
                borderRadius: 4, padding: 12, marginBottom: 14, fontSize: 13,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Error</div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 12, wordBreak: 'break-word' }}>
                  {researchError}
                </div>
              </div>
            )}

            {/* Save summary (after submit) */}
            {!researchLoading && researchSummary && (
              <div style={{
                background: researchSummary.failed.length === 0 ? '#082a14' : '#2a2008',
                border: `1px solid ${researchSummary.failed.length === 0 ? COLORS.green : COLORS.amber}`,
                borderRadius: 4, padding: 12, marginBottom: 14, fontSize: 13,
              }}>
                <div style={{ color: researchSummary.failed.length === 0 ? COLORS.green : COLORS.amber, fontWeight: 700, marginBottom: 6 }}>
                  {researchSummary.failed.length === 0
                    ? `✓ Added ${researchSummary.added.length} parallel${researchSummary.added.length === 1 ? '' : 's'}`
                    : `Added ${researchSummary.added.length}, failed ${researchSummary.failed.length}`}
                </div>
                {researchSummary.added.length > 0 && (
                  <div style={{ color: COLORS.muted, fontSize: 12, marginBottom: 4 }}>
                    Added: {researchSummary.added.map(a => `${a.name}${a.print_run ? ` /${a.print_run}` : ''}`).join(', ')}
                  </div>
                )}
                {researchSummary.failed.length > 0 && (
                  <div style={{ color: COLORS.red, fontSize: 12 }}>
                    {researchSummary.failed.map((f, i) => (
                      <div key={i}>✗ {f.name} ({f.slug}): {f.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Proposals table */}
            {!researchLoading && !researchSummary && researchProposals.length > 0 && (
              <>
                <div style={{ color: COLORS.muted, fontSize: 12, marginBottom: 10 }}>
                  {researchProposals.length} proposal{researchProposals.length === 1 ? '' : 's'} found.
                  Check the ones to add, edit fields if needed, then click ADD SELECTED.
                  Existing parallels are not shown.
                </div>

                <div style={{ border: `1px solid ${COLORS.muted}66`, borderRadius: 4, overflow: 'hidden' }}>
                  {/* Header row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 1.5fr 1fr 80px 90px',
                    gap: 8, padding: '8px 12px',
                    background: COLORS.bg,
                    fontFamily: 'Orbitron, sans-serif', fontSize: 10, letterSpacing: 1, color: COLORS.gold,
                    borderBottom: `1px solid ${COLORS.muted}66`,
                  }}>
                    <div></div>
                    <div>NAME</div>
                    <div>SLUG</div>
                    <div style={{ textAlign: 'right' }}>PRINT RUN</div>
                    <div style={{ textAlign: 'right' }}>CONFIDENCE</div>
                  </div>

                  {/* Proposal rows */}
                  {researchProposals.map((p) => {
                    const hasWarning = p.warnings && p.warnings.length > 0;
                    const rowBg = p.slug_already_exists ? '#2a0808' : (p.checked ? '#082814' : COLORS.bg);
                    return (
                      <div key={p.id} style={{
                        display: 'grid',
                        gridTemplateColumns: '36px 1.5fr 1fr 80px 90px',
                        gap: 8, padding: '8px 12px',
                        background: rowBg,
                        borderBottom: `1px solid ${COLORS.muted}33`,
                        alignItems: 'center',
                      }}>
                        <div>
                          <input
                            type="checkbox"
                            checked={p.checked}
                            onChange={() => toggleProposal(p.id)}
                            disabled={researchSaving}
                            style={{ cursor: researchSaving ? 'not-allowed' : 'pointer' }}
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={p.name}
                            onChange={(e) => updateProposal(p.id, { name: e.target.value })}
                            disabled={researchSaving}
                            style={{
                              ...formInputStyle,
                              padding: '5px 8px',
                              fontSize: 12,
                              borderColor: p.name.trim() ? `${COLORS.muted}66` : COLORS.red,
                            }}
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={p.slug}
                            onChange={(e) => updateProposal(p.id, { slug: e.target.value })}
                            placeholder={p.suggested_slug_was_null ? 'Enter slug' : ''}
                            disabled={researchSaving}
                            style={{
                              ...formInputStyle,
                              padding: '5px 8px',
                              fontSize: 12,
                              borderColor: p.slug.trim() ? `${COLORS.muted}66` : COLORS.red,
                            }}
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={p.print_run == null ? '' : String(p.print_run)}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              if (v === '') { updateProposal(p.id, { print_run: null }); return; }
                              const n = parseInt(v, 10);
                              if (!isNaN(n) && n > 0) updateProposal(p.id, { print_run: n });
                            }}
                            placeholder="∞"
                            disabled={researchSaving}
                            style={{
                              ...formInputStyle,
                              padding: '5px 8px',
                              fontSize: 12,
                              textAlign: 'right',
                            }}
                          />
                        </div>
                        <div style={{
                          textAlign: 'right',
                          fontFamily: 'Orbitron, sans-serif',
                          fontSize: 10, letterSpacing: 1, fontWeight: 700,
                          color: p.confidence === 'high' ? COLORS.green : p.confidence === 'medium' ? COLORS.gold : COLORS.red,
                        }}>
                          {(p.confidence || 'low').toUpperCase()}
                        </div>

                        {/* Warnings row (spans all columns) */}
                        {hasWarning && (
                          <div style={{
                            gridColumn: '1 / span 5',
                            color: p.slug_already_exists ? COLORS.red : COLORS.amber,
                            fontSize: 11,
                            paddingLeft: 36,
                            marginTop: 4,
                          }}>
                            ⚠ {p.warnings.join(' · ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Empty proposals */}
            {!researchLoading && !researchError && !researchSummary && researchProposals.length === 0 && (
              <div style={{ color: COLORS.muted, padding: 30, textAlign: 'center', fontSize: 13 }}>
                No new parallels proposed. The page may not have listed any for this sub-set, or all parallels listed already exist.
              </div>
            )}

            {/* Action buttons */}
            {!researchLoading && !researchSummary && (
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                <button
                  onClick={closeResearch}
                  disabled={researchSaving}
                  style={{
                    ...buttonOutlineStyle,
                    color: COLORS.muted,
                    borderColor: COLORS.muted,
                    opacity: researchSaving ? 0.5 : 1,
                    cursor: researchSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  CANCEL
                </button>
                {researchProposals.length > 0 && (
                  <button
                    onClick={submitResearch}
                    disabled={researchSaving || researchProposals.every(p => !p.checked)}
                    style={{
                      ...buttonPrimaryStyle,
                      opacity: researchSaving ? 0.6 : 1,
                      cursor: researchSaving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {researchSaving ? 'ADDING...' : `ADD SELECTED (${researchProposals.filter(p => p.checked).length})`}
                  </button>
                )}
              </div>
            )}

            {/* Close button after summary */}
            {researchSummary && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <button onClick={closeResearch} style={buttonPrimaryStyle}>
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared styles
// -----------------------------------------------------------------------------

const containerStyle = {
  minHeight: '100vh', background: COLORS.bg,
  padding: '24px 16px', fontFamily: 'Rajdhani, sans-serif', color: '#fff',
};
const headerTitle = {
  fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
  fontSize: 20, letterSpacing: 1,
};
const buttonPrimaryStyle = {
  padding: '10px 18px', background: COLORS.gold, color: COLORS.bg, border: 'none',
  borderRadius: 4, fontFamily: 'Orbitron, sans-serif', fontSize: 12,
  letterSpacing: 1.5, fontWeight: 700, cursor: 'pointer',
};
const buttonOutlineStyle = {
  padding: '10px 18px', background: 'transparent', color: COLORS.cyan,
  border: `1px solid ${COLORS.cyan}`, borderRadius: 4,
  fontFamily: 'Orbitron, sans-serif', fontSize: 12, letterSpacing: 1.5, cursor: 'pointer',
};
const formLabelStyle = {
  fontFamily: 'Orbitron, sans-serif', fontSize: 10,
  color: COLORS.gold, letterSpacing: 1, marginBottom: 4, display: 'block',
};
const formInputStyle = {
  width: '100%', background: COLORS.bg, color: '#fff',
  border: `1px solid ${COLORS.muted}66`, borderRadius: 4,
  padding: '8px 10px', fontFamily: 'Share Tech Mono, monospace',
  fontSize: 13, boxSizing: 'border-box',
};
